/**
 * Portal Events Page
 * Event management for PFEVT (Event Promoter) users
 */

import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { PermissionGate } from '../../components/PermissionGate';
import portalService from '../../services/portal.service';
import type { PortalEvent, PortalTicket } from '../../types/portal.types';
import { nowCv, fmtParede } from '../../utils/tempoCv';

interface EventFormData {
  NOME: string;
  DESCRICAO: string;
  DATA_INICIO: string;
  DATA_FIM?: string;
  LOCAL?: string;
  ENDERECO?: string;
  CAPACIDADE?: number;
  ESTADO: 'A' | 'I' | 'C';
  ESGOTADO?: boolean;
  imagem?: string;      // base64
  mimeType?: string;
}

const PortalEvents: React.FC = () => {
  const { hasPermission } = useAuth();
  const [events, setEvents] = useState<PortalEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [showTicketsModal, setShowTicketsModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState<PortalEvent | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<PortalEvent | null>(null);
  const [tickets, setTickets] = useState<PortalTicket[]>([]);
  const [validateCode, setValidateCode] = useState('');
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [formData, setFormData] = useState<EventFormData>({
    NOME: '',
    DESCRICAO: '',
    DATA_INICIO: '',
    ESTADO: 'A',
  });

  const loadEvents = async () => {
    try {
      setLoading(true);
      const res = await portalService.getEvents();
      setEvents(res.data || []);
    } catch (err: any) {
      setError(err.message || 'Erro ao carregar eventos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEvents();
  }, []);

  const loadTickets = async (eventId: string) => {
    try {
      const res = await portalService.getEventTickets(eventId);
      setTickets(res.data || []);
    } catch (err: any) {
      setError(err.message || 'Erro ao carregar bilhetes');
    }
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const dataUrl = ev.target?.result as string;
      const base64 = dataUrl.split(',')[1];
      setFormData(prev => ({ ...prev, imagem: base64, mimeType: file.type }));
      setImagePreview(dataUrl);
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingEvent) {
        await portalService.updateEvent(editingEvent.ID, formData);
      } else {
        await portalService.createEvent(formData);
      }
      setShowModal(false);
      setEditingEvent(null);
      resetForm();
      loadEvents();
    } catch (err: any) {
      setError(err.message || 'Erro ao guardar evento');
    }
  };

  const handleEdit = (event: PortalEvent) => {
    setEditingEvent(event);
    const imgUrl = (event as any).IMAGEM_URL || null;
    setImagePreview(imgUrl);
    setFormData({
      NOME: event.NOME,
      DESCRICAO: event.DESCRICAO || '',
      DATA_INICIO: event.DATA_INICIO ? event.DATA_INICIO.slice(0, 16) : '',
      DATA_FIM: event.DATA_FIM ? event.DATA_FIM.slice(0, 16) : undefined,
      LOCAL: event.LOCAL,
      ENDERECO: event.ENDERECO,
      CAPACIDADE: event.CAPACIDADE,
      ESTADO: event.ESTADO,
      ESGOTADO: event.ESGOTADO ?? false,
    });
    setShowModal(true);
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Tem certeza que deseja eliminar este evento?')) return;
    try {
      await portalService.deleteEvent(id);
      loadEvents();
    } catch (err: any) {
      setError(err.message || 'Erro ao eliminar evento');
    }
  };

  const handleViewTickets = async (event: PortalEvent) => {
    setSelectedEvent(event);
    await loadTickets(event.ID);
    setShowTicketsModal(true);
  };

  const handleValidateTicket = async () => {
    if (!selectedEvent || !validateCode.trim()) return;
    try {
      await portalService.validateTicket(selectedEvent.ID, validateCode);
      setValidateCode('');
      loadTickets(selectedEvent.ID);
      alert('Bilhete validado com sucesso!');
    } catch (err: any) {
      setError(err.message || 'Erro ao validar bilhete');
    }
  };

  const resetForm = () => {
    setFormData({ NOME: '', DESCRICAO: '', DATA_INICIO: '', ESTADO: 'A', ESGOTADO: false });
    setImagePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const getEstadoLabel = (estado: string) => {
    switch (estado) {
      case 'A': return { text: 'Ativo', class: 'bg-green-100 text-green-800' };
      case 'I': return { text: 'Inativo', class: 'bg-gray-100 text-gray-800' };
      case 'C': return { text: 'Cancelado', class: 'bg-red-100 text-red-800' };
      default: return { text: estado, class: 'bg-gray-100 text-gray-800' };
    }
  };

  const getTicketEstadoLabel = (estado: string) => {
    switch (estado) {
      case 'ATIVO': return { text: 'Ativo', class: 'bg-green-100 text-green-800' };
      case 'USADO': return { text: 'Usado', class: 'bg-blue-100 text-blue-800' };
      case 'EXPIRADO': return { text: 'Expirado', class: 'bg-gray-100 text-gray-800' };
      case 'CANCELADO': return { text: 'Cancelado', class: 'bg-red-100 text-red-800' };
      default: return { text: estado, class: 'bg-gray-100 text-gray-800' };
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pagali-primary"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Meus Eventos</h1>
          <p className="text-gray-600">Gerir eventos e bilhetes</p>
        </div>
        <PermissionGate permission="MY_EVENTS_CREATE">
          <button
            onClick={() => { resetForm(); setEditingEvent(null); setShowModal(true); }}
            className="px-4 py-2 bg-pagali-primary text-white rounded-lg hover:bg-pagali-primary/80 flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Novo Evento
          </button>
        </PermissionGate>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-600">{error}</p>
          <button onClick={() => setError(null)} className="text-sm text-red-500 underline mt-1">Fechar</button>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-sm text-gray-500">Total Eventos</p>
          <p className="text-2xl font-semibold">{events.length}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-sm text-gray-500">Ativos</p>
          <p className="text-2xl font-semibold text-green-600">{events.filter(e => e.ESTADO === 'A' && !e.ESGOTADO && (e.DATA_FIM ? new Date(e.DATA_FIM) >= nowCv() : new Date(e.DATA_INICIO) >= nowCv())).length}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-sm text-gray-500">Esgotados</p>
          <p className="text-2xl font-semibold text-amber-600">{events.filter(e => e.ESGOTADO).length}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-sm text-gray-500">Total Bilhetes</p>
          <p className="text-2xl font-semibold">
            {events.reduce((sum, e) => sum + (e.totalCapacidade || e.totalBilhetes || 0), 0)}
          </p>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-sm text-gray-500">Bilhetes Usados</p>
          <p className="text-2xl font-semibold text-blue-600">
            {events.reduce((sum, e) => sum + (e.bilhetesUsados || 0), 0)}
          </p>
        </div>
      </div>

      {/* Events Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {events.length === 0 ? (
          <div className="col-span-full bg-white rounded-lg shadow p-12 text-center text-gray-500">
            Nenhum evento encontrado. Clique em "Novo Evento" para criar.
          </div>
        ) : (
          events.map((event) => {
            const estado = getEstadoLabel(event.ESTADO);
            const dataInicio = new Date(event.DATA_INICIO);
            const dataFim = event.DATA_FIM ? new Date(event.DATA_FIM) : null;
            const isPast = dataFim ? dataFim < nowCv() : dataInicio < nowCv();
            const imgUrl = (event as any).IMAGEM_URL;
            const totalCapacidade = (event as any).totalCapacidade || 0;
            return (
              <div key={event.ID} className="bg-white rounded-lg shadow overflow-hidden">
                {imgUrl ? (
                  <img src={imgUrl} alt={event.NOME} className="w-full h-48 object-cover" />
                ) : (
                  <div className="w-full h-48 bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
                    <svg className="w-16 h-16 text-white/50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                )}
                <div className="p-6">
                  <div className="flex items-start justify-between">
                    <h3 className="text-lg font-semibold text-gray-900">{event.NOME}</h3>
                    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                      event.ESTADO === 'C' ? 'bg-red-100 text-red-800' :
                      isPast ? 'bg-gray-200 text-gray-600' :
                      event.ESGOTADO ? 'bg-amber-100 text-amber-800' :
                      estado.class
                    }`}>
                      {event.ESTADO === 'C' ? 'Cancelado' : isPast ? 'Encerrado' : event.ESGOTADO ? 'Esgotado' : estado.text}
                    </span>
                  </div>
                  <div className="mt-2 space-y-1 text-sm text-gray-600">
                    <div className="flex items-center gap-2">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <span className={isPast ? 'text-gray-400' : ''}>
                        {fmtParede(dataInicio, { day: '2-digit', month: '2-digit', year: 'numeric' })} {fmtParede(dataInicio, { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                    {event.LOCAL && (
                      <div className="flex items-center gap-2">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        <span>{event.LOCAL}</span>
                      </div>
                    )}
                    {event.CAPACIDADE && (
                      <div className="flex items-center gap-2">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        <span>Capacidade: {event.CAPACIDADE}</span>
                      </div>
                    )}
                  </div>
                  <div className="mt-4 flex items-center gap-4 text-sm">
                    <span className="text-gray-500">
                      <span className="font-medium text-gray-900">{totalCapacidade}</span> bilhetes disponíveis
                    </span>
                    <span className="text-gray-500">
                      <span className="font-medium text-blue-600">{event.bilhetesUsados || 0}</span> usados
                    </span>
                  </div>
                </div>
                <div className="px-6 py-3 bg-gray-50 border-t border-gray-100 flex justify-between">
                  <PermissionGate permission="MY_TICKETS_VIEW">
                    <button onClick={() => handleViewTickets(event)} className="text-sm text-pagali-primary hover:text-pagali-primary/80">
                      Ver Bilhetes
                    </button>
                  </PermissionGate>
                  <div className="flex gap-4">
                    <PermissionGate permission="MY_EVENTS_EDIT">
                      <button onClick={() => handleEdit(event)} className="text-sm text-gray-600 hover:text-gray-900">Editar</button>
                    </PermissionGate>
                    <PermissionGate permission="MY_EVENTS_DELETE">
                      <button onClick={() => handleDelete(event.ID)} className="text-sm text-red-600 hover:text-red-900">Eliminar</button>
                    </PermissionGate>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Event Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold">{editingEvent ? 'Editar Evento' : 'Novo Evento'}</h2>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {/* Foto de Capa */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Foto de Capa</label>
                {imagePreview && (
                  <img src={imagePreview} alt="Capa" className="w-full h-40 object-cover rounded-lg mb-2" />
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={handleImageChange}
                  className="w-full text-sm text-gray-500 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-pagali-primary/10 file:text-pagali-primary hover:file:bg-pagali-primary/20 cursor-pointer"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nome do Evento *</label>
                <input
                  type="text"
                  required
                  value={formData.NOME}
                  onChange={(e) => setFormData({ ...formData, NOME: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pagali-primary"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Descrição</label>
                <textarea
                  value={formData.DESCRICAO}
                  onChange={(e) => setFormData({ ...formData, DESCRICAO: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pagali-primary"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Data Início *</label>
                  <input
                    type="datetime-local"
                    required
                    value={formData.DATA_INICIO}
                    onChange={(e) => setFormData({ ...formData, DATA_INICIO: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pagali-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Data Fim</label>
                  <input
                    type="datetime-local"
                    value={formData.DATA_FIM || ''}
                    onChange={(e) => setFormData({ ...formData, DATA_FIM: e.target.value || undefined })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pagali-primary"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Local</label>
                  <input
                    type="text"
                    value={formData.LOCAL || ''}
                    onChange={(e) => setFormData({ ...formData, LOCAL: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pagali-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Capacidade</label>
                  <input
                    type="number"
                    min="0"
                    value={formData.CAPACIDADE || ''}
                    onChange={(e) => setFormData({ ...formData, CAPACIDADE: e.target.value ? parseInt(e.target.value) : undefined })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pagali-primary"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Endereço</label>
                <input
                  type="text"
                  value={formData.ENDERECO || ''}
                  onChange={(e) => setFormData({ ...formData, ENDERECO: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pagali-primary"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Estado</label>
                  <select
                    value={formData.ESTADO}
                    onChange={(e) => setFormData({ ...formData, ESTADO: e.target.value as 'A' | 'I' | 'C' })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pagali-primary"
                  >
                    <option value="A">Ativo</option>
                    <option value="I">Inativo</option>
                    <option value="C">Cancelado</option>
                  </select>
                </div>
                <div className="flex items-end pb-1">
                  <label className="flex items-center gap-2 cursor-pointer select-none">
                    <div
                      onClick={() => setFormData({ ...formData, ESGOTADO: !formData.ESGOTADO })}
                      className={`relative w-11 h-6 rounded-full transition-colors ${formData.ESGOTADO ? 'bg-amber-500' : 'bg-gray-300'}`}
                    >
                      <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${formData.ESGOTADO ? 'translate-x-5' : ''}`} />
                    </div>
                    <span className="text-sm font-medium text-gray-700">Esgotado</span>
                  </label>
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">Cancelar</button>
                <button type="submit" className="px-4 py-2 bg-pagali-primary text-white rounded-lg hover:bg-pagali-primary/80">
                  {editingEvent ? 'Guardar' : 'Criar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Tickets Modal */}
      {showTicketsModal && selectedEvent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl mx-4 max-h-[90vh] overflow-hidden flex flex-col">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold">Bilhetes - {selectedEvent.NOME}</h2>
                <p className="text-sm text-gray-500">
                  {tickets.length} bilhetes | {tickets.filter(t => t.ESTADO === 'USADO').length} usados
                </p>
              </div>
              <button onClick={() => setShowTicketsModal(false)} className="text-gray-400 hover:text-gray-600">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <PermissionGate permission="MY_TICKETS_MANAGE">
              <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder="Código do bilhete..."
                    value={validateCode}
                    onChange={(e) => setValidateCode(e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pagali-primary"
                  />
                  <button
                    onClick={handleValidateTicket}
                    disabled={!validateCode.trim()}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                  >
                    Validar Bilhete
                  </button>
                </div>
              </div>
            </PermissionGate>
            <div className="flex-1 overflow-y-auto p-6">
              {tickets.length === 0 ? (
                <p className="text-center text-gray-500 py-8">Nenhum bilhete vendido ainda.</p>
              ) : (
                <div className="space-y-3">
                  {tickets.map((ticket) => {
                    const estado = getTicketEstadoLabel(ticket.ESTADO);
                    return (
                      <div key={ticket.ID} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                        <div>
                          <p className="font-medium">{ticket.CODIGO}</p>
                          <p className="text-sm text-gray-500">
                            {ticket.CLIENTE_NOME || 'Cliente anónimo'} - {ticket.TIPO_BILHETE || 'Geral'}
                          </p>
                          <p className="text-xs text-gray-400">
                            Comprado: {new Date(ticket.DATA_COMPRA).toLocaleDateString('pt-CV', { timeZone: 'Atlantic/Cape_Verde' })}
                          </p>
                        </div>
                        <div className="text-right">
                          <span className={`px-2 py-1 text-xs font-semibold rounded-full ${estado.class}`}>{estado.text}</span>
                          <p className="text-sm font-medium mt-1">{ticket.PRECO?.toLocaleString('pt-CV')} ECV</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PortalEvents;
